import { useState } from 'react'
import { useGetMany, useGetList, useNotify } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import MuiTextField from '@mui/material/TextField'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'

const AssignSeriesAside = ({ seriesIds, onAdd }) => {
  const notify = useNotify()
  const [searchText, setSearchText] = useState('')
  const [selected, setSelected] = useState(null)

  const { data: searchResults = [] } = useGetList('kid-series', {
    filter: searchText ? { title: { $iLike: `%${searchText}%` } } : {},
    pagination: { page: 1, perPage: 20 },
    sort: { field: 'title', order: 'ASC' },
  })

  const options = searchResults.filter(s => !seriesIds.includes(s.id))

  const handleAdd = () => {
    if (!selected) return
    if (seriesIds.includes(selected.id)) {
      notify('Series already added', { type: 'warning' })
      return
    }
    onAdd(selected)
    setSelected(null)
    setSearchText('')
  }

  return (
    <Box sx={{ margin: '0.5rem', width: 400, flexShrink: 0 }}>
      <Box sx={{ p: 2 }}>
        <Autocomplete
          options={options}
          getOptionLabel={option => option.title || ''}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          inputValue={searchText}
          onInputChange={(_, value) => setSearchText(value)}
          filterOptions={x => x}
          renderInput={params => (
            <MuiTextField
              {...params}
              label="Select Series"
              variant="standard"
              fullWidth
            />
          )}
        />
      </Box>
      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          type="button"
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          disabled={!selected}
          onClick={handleAdd}>
          Save
        </Button>
      </Box>
    </Box>
  )
}

const KidsStorySeriesInput = ({ source = 'seriesIds' }) => {
  const { setValue } = useFormContext()
  const seriesIds = useWatch({ name: source }) || []

  const { data: fetchedSeries = [] } = useGetMany(
    'kid-series',
    { ids: seriesIds },
    { enabled: seriesIds.length > 0 },
  )

  const seriesMap = {}
  fetchedSeries.forEach(s => {
    if (s) seriesMap[s.id] = s
  })

  const handleAdd = series => {
    setValue(source, [...seriesIds, series.id], { shouldDirty: true })
  }

  const handleRemove = id => {
    setValue(
      source,
      seriesIds.filter(sid => sid !== id),
      { shouldDirty: true },
    )
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Series ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {seriesIds.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 2 }}>
                    No series added yet
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {seriesIds.map(id => (
              <TableRow key={id}>
                <TableCell>{id}</TableCell>
                <TableCell>{seriesMap[id]?.title || '—'}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemove(id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <AssignSeriesAside seriesIds={seriesIds} onAdd={handleAdd} />
    </Box>
  )
}

export default KidsStorySeriesInput
